/**
 * Zod schema for "Add Jira Connection" (Charter M3 migration), imported by
 * `JiraConnectionForm.tsx`, never redefined (form-standard.context.md §3). Rule IDs `VJ-*` mirror
 * the pre-migration hand-rolled `validate()` exactly.
 */
import { z } from 'zod';
import type { CreateJiraConnectionRequest, JiraAuthType, JiraDeploymentType } from '../types';
import { JIRA_AUTH_TYPE_OPTIONS, JIRA_DEPLOYMENT_TYPE_OPTIONS, isParseableUrl } from './connectionFormFields';

// reason: JIRA_*_OPTIONS are runtime-verified non-empty arrays of the exported union types;
// zod's z.enum() requires a literal non-empty tuple type.
const DEPLOYMENT_TYPE_TUPLE = JIRA_DEPLOYMENT_TYPE_OPTIONS.map((o) => o.value) as [
  JiraDeploymentType,
  ...JiraDeploymentType[],
];
const AUTH_TYPE_TUPLE = JIRA_AUTH_TYPE_OPTIONS.map((o) => o.value) as [JiraAuthType, ...JiraAuthType[]];

export const JiraConnectionSchema = z.object({
  // VJ-01
  name: z.string().trim().min(1, 'Connection name is required'),
  // VJ-02 (required) / VJ-03 (must be a parseable URL)
  instanceUrl: z
    .string()
    .trim()
    .min(1, 'Instance URL is required')
    .refine(isParseableUrl, { message: 'Please enter a valid URL' }),
  deploymentType: z.enum(DEPLOYMENT_TYPE_TUPLE),
  authType: z.enum(AUTH_TYPE_TUPLE),
  // VJ-04
  username: z.string().trim().min(1, 'Username is required'),
  // VJ-05
  apiToken: z.string().trim().min(1, 'API token is required'),
});

/** The shape RHF's `useForm<T>`/`defaultValues` are typed against (pre-transform). */
export type JiraConnectionFormValues = z.input<typeof JiraConnectionSchema>;
/** The shape `handleSubmit(onValid)` receives (post-transform — trimmed). */
export type JiraConnectionFormOutput = z.output<typeof JiraConnectionSchema>;

// Compile-time proof the schema's output assembles a valid request DTO.
type _AssertAssignable = JiraConnectionFormOutput extends CreateJiraConnectionRequest ? true : false;
const _assertAssignable: _AssertAssignable = true;
void _assertAssignable;
